/**
 * SecurityTreeNode — Custom node renderer for react-arborist.
 *
 * Renders a single tree node with:
 * - Expand/collapse arrow for internal nodes
 * - 5-state access icon (none, read, write, partial-read, partial-write)
 * - Label text
 *
 * The access icon click triggers `onActivate` on the Tree, which is
 * wired to the hook's `toggle` function at the SecurityTree level.
 */

import type { NodeRendererProps } from 'react-arborist';
import type { AccessState, SecurityNodeType } from './types';

export interface SecurityTreeNodeData {
  id: string;
  name: string;
  nodeType: SecurityNodeType;
  accessState: AccessState;
  children?: SecurityTreeNodeData[];
}

interface AccessIconProps {
  state: AccessState;
  isLeaf: boolean;
}

function AccessIcon({ state, isLeaf }: AccessIconProps) {
  const size = 18;
  const baseClass = 'inline-flex items-center justify-center shrink-0';

  switch (state) {
    case 'write':
      return (
        <svg width={size} height={size} viewBox="0 0 18 18" className={baseClass}>
          <rect x="2" y="2" width="14" height="14" rx="2" fill="#16a34a" />
          <path d="M5 9l3 3 5-5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'read':
      return (
        <svg width={size} height={size} viewBox="0 0 18 18" className={baseClass}>
          <rect x="2" y="2" width="14" height="14" rx="2" fill="#ea580c" />
          <path d="M5 9l3 3 5-5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'partial-write':
      return (
        <svg width={size} height={size} viewBox="0 0 18 18" className={baseClass}>
          <rect x="2" y="2" width="14" height="14" rx="2" fill="none" stroke="#16a34a" strokeWidth="2" />
          <rect x="5" y="5" width="8" height="8" rx="1" fill="#16a34a" opacity="0.5" />
        </svg>
      );
    case 'partial-read':
      return (
        <svg width={size} height={size} viewBox="0 0 18 18" className={baseClass}>
          <rect x="2" y="2" width="14" height="14" rx="2" fill="none" stroke="#ea580c" strokeWidth="2" />
          <rect x="5" y="5" width="8" height="8" rx="1" fill="#ea580c" opacity="0.5" />
        </svg>
      );
    case 'none':
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 18 18" className={baseClass}>
          <rect x="2" y="2" width="14" height="14" rx="2" fill="none" stroke={isLeaf ? '#9ca3af' : '#6b7280'} strokeWidth="2" />
        </svg>
      );
  }
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      className={`shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`}
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SecurityTreeNode({
  node,
  style,
}: NodeRendererProps<SecurityTreeNodeData>) {
  const data = node.data;
  const isLeaf = node.isLeaf;

  return (
    <div
      style={style}
      className={`flex items-center gap-1.5 px-2 h-full select-none
        ${node.isFocused ? 'bg-primary-faded-10' : ''}
        hover:bg-container-bg-hover`}
    >
      {/* Expand/Collapse arrow (only for internal nodes) */}
      <span
        className={`w-4 ${isLeaf ? '' : 'cursor-pointer text-foreground-faded'}`}
        onClick={(e) => {
          if (!isLeaf) {
            e.stopPropagation();
            node.toggle();
          }
        }}
      >
        {!isLeaf && <ChevronIcon isOpen={node.isOpen} />}
      </span>

      {/* Access state icon — all states are clickable */}
      <span
        className="cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          node.activate();
        }}
      >
        <AccessIcon state={data.accessState} isLeaf={isLeaf} />
      </span>

      {/* Label */}
      <span className="truncate text-sm text-foreground">
        {data.name}
      </span>
    </div>
  );
}
